/**
 * Shared label tile (Run Batch Label Printing feature).
 *
 * The QR render appears in three places in the SPA: the per-row "View label"
 * tile in {@link BrowseSection}'s `RowLabel`, the multi-select sheet tile in
 * `LabelSheet` (both in `StoragePage.tsx`), and the new "Print this run's label"
 * panel on `BatchReviewPage`. To avoid duplicating the render a third time, the
 * common `.tcvs-print-label` tile — a dashed-border container, a prominent
 * human-readable code above a `qrcode.react` `QRCodeSVG` showing the QR_Token —
 * lives here as a single component (design "Extracted
 * shared label tile — LabelTile component"; Req 4.4, 6.3, 6.5).
 *
 * This is a pure render extraction: it preserves the existing `.tcvs-print-label`
 * class (and therefore the `@media print` isolation in `index.html`), the exact
 * `QRCodeSVG` props (`size`/`level="Q"`/`includeMargin`/`style`), and the
 * caption/token/entityId markup. The caller-supplied Print control stays a
 * sibling passed via `children`, exactly as the call sites render it today — no
 * change to the print mechanics (Out of Scope respected).
 *
 * Call sites differ only in cosmetic sizing (`RowLabel` caps the container,
 * sheets fix a width, and detail pages use compact previews), so the container
 * style and QR `maxWidth` are props.
 *
 * Validates: Requirements 4.4, 6.3, 6.5
 */

import { useCallback, useState } from 'react';
import type * as React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { labelCaption } from './label-caption';

/** The default dashed-border tile container style shared by every call site. */
const DEFAULT_CONTAINER_STYLE: React.CSSProperties = {
  border: '2px dashed #333',
  padding: 16,
  textAlign: 'center',
};

/**
 * A "Download PDF" control that produces a 2.25in x 1.25in label PDF for import
 * into LabeLife (or any PDF-import label app). The heavy `jspdf` + `qrcode`
 * dependencies are dynamically imported only on click, so they never load on
 * first paint. Wrapped by the caller in `.tcvs-print-controls` so it is hidden
 * from the browser print output.
 */
function DownloadPdfButton({
  token,
  entityId,
  shortCode,
}: {
  token: string;
  entityId: string;
  shortCode?: string;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDownload = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { downloadLabelPdf } = await import('./label-pdf');
      await downloadLabelPdf(token, entityId, shortCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'PDF generation failed');
    } finally {
      setBusy(false);
    }
  }, [token, entityId, shortCode]);

  return (
    <span>
      <button type="button" onClick={() => void onDownload()} disabled={busy}>
        {busy ? 'Building PDF…' : 'Save PDF'}
      </button>
      {error !== null ? (
        <span className="tcvs-error" style={{ marginLeft: 8 }}>
          {error}
        </span>
      ) : null}
    </span>
  );
}

export function LabelTile({
  token,
  entityId,
  shortCode,
  size = 160,
  qrMaxWidth = 180,
  containerStyle = DEFAULT_CONTAINER_STYLE,
  children,
}: {
  /** The QR_Token to encode and display (for a batch this equals the entityId). */
  token: string;
  /** The Storage_Entity_Id (carried for the PDF filename / debugging). */
  entityId: string;
  /** A card's human-friendly short code; when present on a card token it is the
   *  caption code instead of a UUID slice. */
  shortCode?: string;
  /** The `QRCodeSVG` `size` prop; defaults smaller so the larger code has room. */
  size?: number;
  /** The QR image `maxWidth` cap. */
  qrMaxWidth?: number;
  /** The `.tcvs-print-label` container style; each call site passes its exact style. */
  containerStyle?: React.CSSProperties;
  /** Optional sibling controls rendered inside the tile (e.g. a Print button). */
  children?: React.ReactNode;
}): JSX.Element {
  // Human-readable caption (e.g. `Batch: 3FM8E1`) shown prominently in place of
  // the long opaque token, which is hard for a picker to read. The QR still
  // encodes the full `token`, so scanning is unaffected.
  const caption = labelCaption(token, shortCode);
  return (
    // On screen the tile is a 2-column GRID: the large `.tcvs-mono` picker code
    // (col 1, row 1) sits above the smaller QR `<svg>` (col 1, row 2), and the
    // controls column sits to the RIGHT (col 2, spanning both rows).
    //
    // The QR `<svg>` and the caption stay DIRECT children of `.tcvs-print-label`
    // so the `@media print` rules in index.html — which target
    // `.tcvs-print-label svg` / `> div:not(.tcvs-mono)` / `.tcvs-mono` and force
    // the container back to `display:flex; flex-direction:column !important`
    // (overriding this grid) — keep working unchanged. The controls are hidden
    // in print via `.tcvs-print-controls` + the `.tcvs-print-label button {
    // display:none }` rule.
    <div
      className="tcvs-print-label"
      style={{
        display: 'grid',
        gridTemplateColumns: 'auto auto',
        columnGap: 16,
        rowGap: 8,
        alignItems: 'center',
        justifyContent: 'start',
        ...containerStyle,
      }}
    >
      <div
        className="tcvs-mono"
        style={{
          display: 'grid',
          gridTemplateColumns: '28px auto 28px',
          alignItems: 'center',
          fontWeight: 800,
          gridColumn: 1,
          gridRow: 1,
          lineHeight: 1,
          textAlign: 'center',
        }}
      >
        <span
          className="tcvs-label-prefix"
          style={{
            fontSize: 20,
            fontWeight: 700,
            justifySelf: 'center',
            position: 'relative',
            top: 10,
            transform: 'rotate(-90deg)',
            transformOrigin: 'center',
            whiteSpace: 'nowrap',
          }}
        >
          {caption.typeLabel}:
        </span>
        <span className="tcvs-label-code" style={{ fontSize: 40, fontWeight: 800 }}>
          {caption.code}
        </span>
        <span aria-hidden="true" />
      </div>
      <QRCodeSVG
        value={token}
        size={size}
        level="Q"
        includeMargin
        style={{
          width: Math.min(qrMaxWidth, size),
          height: 'auto',
          maxWidth: '100%',
          gridColumn: 1,
          gridRow: 2,
          justifySelf: 'center',
        }}
      />
      {/* Right-hand controls column: Save PDF + any caller-supplied Print button,
          stacked, spanning both QR rows so it sits to the QR's right. */}
      <div
        className="tcvs-print-controls"
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          gridColumn: 2,
          gridRow: '1 / span 2',
        }}
      >
        <DownloadPdfButton token={token} entityId={entityId} shortCode={shortCode} />
        {children}
      </div>
    </div>
  );
}
